import type { ChartData } from '../types';
import { humanise, isNumeric, looksTemporal } from './format';

/**
 * Turns a raw query result into the shape a chart form can draw.
 *
 * The backend catalogue is ~60 charts and growing, and each one returns its own
 * columns. Hand-wiring a column mapping per chart would mean touching this app
 * every time the data team adds one, so instead every form draws a `Frame` and
 * this file works out which column is the category, which are measures, and
 * whether a dimension should be pivoted into separate series.
 *
 * Inference is a default, not a straitjacket: a saved chart can pin the roles
 * (see `FrameHints`) when the guess is wrong for that particular query.
 */

export interface Series {
  name: string;
  /** One value per category. `null` is a genuine gap, drawn as a break. */
  values: Array<number | null>;
}

export interface Frame {
  /** X axis / row labels. */
  categories: string[];
  categoryName: string;
  series: Series[];
  /** Name for the measure axis. Empty when several unlike measures are plotted. */
  measureName: string;
  /** Categories are dates, so they order naturally and get date-shortened labels. */
  temporal: boolean;
  /** Rows the query returned. */
  rowCount: number;
  /**
   * Distinct categories the query returned, before any cap. Not the same as
   * `rowCount` once a dimension has been pivoted into series — a 12-month,
   * 2-platform result is 24 rows and 12 categories, and saying "the first 12 of
   * 24" would be nonsense.
   */
  categoryCount: number;
  /** True when categories were capped and a tail was dropped. */
  truncated: boolean;
  /**
   * Numeric columns left off the plot because they are on a different scale.
   * They are still in the result, and the table view still shows them — this
   * list exists so the chart can say so rather than silently hiding a column.
   */
  omitted: string[];
}

export interface FrameHints {
  /** Column to use for the category axis. */
  category?: string;
  /** Columns to plot. Defaults to every numeric column. */
  measures?: string[];
  /** Column to pivot into one series per distinct value. */
  seriesBy?: string;
  /** Cap on categories. Bars and lines want different limits. */
  limit?: number;
}

/** A dimension with more distinct values than this is a label, not a series key. */
const MAX_PIVOT_CARDINALITY = 8;
const DEFAULT_LIMIT = 200;
/**
 * How far apart two measures' typical values may be before they stop belonging
 * on the same axis. A rank of 1-5 plotted beside downloads in the millions is a
 * flat line along the baseline: technically drawn, actually invisible.
 */
const MAX_SCALE_RATIO = 100;

const columnIsNumeric = (data: ChartData, index: number): boolean => {
  const sample = data.rows.slice(0, 30).map((row) => row[index]);
  const present = sample.filter((v) => v !== null && v !== undefined && v !== '');
  if (present.length === 0) return false;
  return present.filter(isNumeric).length / present.length > 0.6;
};

const toNumber = (value: unknown): number | null => (isNumeric(value) ? value : null);

/** Typical size of a column. Median, so one outlier row cannot speak for it. */
const magnitude = (data: ChartData, column: string): number => {
  const index = data.columns.indexOf(column);
  const values = data.rows
    .map((row) => row[index])
    .filter(isNumeric)
    .map(Math.abs)
    .filter((v) => v > 0)
    .sort((a, b) => a - b);

  if (!values.length) return 0;
  return values[Math.floor(values.length / 2)];
};

/**
 * Keeps only the measures that can honestly share one axis.
 *
 * The alternative every BI tool reaches for is a second y-axis, and it is the
 * single most misleading thing a chart can do: the alignment between the two
 * scales is arbitrary, so the chart invents a correlation that is not in the
 * data. Dropping the odd-scale columns — and saying so — is the honest version.
 * Nothing is lost: the table view still has every column.
 */
const comparableMeasures = (
  data: ChartData,
  measures: string[],
): { kept: string[]; omitted: string[] } => {
  if (measures.length < 2) return { kept: measures, omitted: [] };

  const sized = measures.map((m) => ({ m, size: magnitude(data, m) }));
  const largest = Math.max(...sized.map((s) => s.size));
  if (largest === 0) return { kept: measures, omitted: [] };

  const kept = sized.filter((s) => s.size * MAX_SCALE_RATIO >= largest).map((s) => s.m);
  const omitted = measures.filter((m) => !kept.includes(m));
  return { kept, omitted };
};

const label = (value: unknown): string =>
  value === null || value === undefined || value === '' ? '—' : String(value);

export const EMPTY_FRAME: Frame = {
  categories: [],
  categoryName: '',
  series: [],
  measureName: '',
  temporal: false,
  rowCount: 0,
  categoryCount: 0,
  truncated: false,
  omitted: [],
};

export const buildFrame = (data: ChartData, hints: FrameHints = {}): Frame => {
  if (!data.columns.length || !data.rows.length) return EMPTY_FRAME;

  const limit = hints.limit ?? DEFAULT_LIMIT;
  const numericColumns = data.columns.filter((_, i) => columnIsNumeric(data, i));
  const dimensionColumns = data.columns.filter((c) => !numericColumns.includes(c));
  const indexOf = (column: string) => data.columns.indexOf(column);

  // An explicit hint is the author's decision and is honoured as given; the
  // scale filter only applies to the inferred set.
  // Filtered against the real columns, because a saved chart outlives the shape
  // of its query: a renamed or dropped column must fall back to the inference,
  // not leave the hint matching nothing. An empty result is therefore the same
  // as no hint at all — without this the chart renders with no series, which
  // reads as "no data" when the data is fine and only the column name moved.
  const named = hints.measures?.filter((m) => data.columns.includes(m));
  const requested = named?.length ? named : undefined;
  const inferred = numericColumns.length ? numericColumns : data.columns.slice(-1);
  const { kept: measures, omitted } = requested
    ? { kept: requested, omitted: [] as string[] }
    : comparableMeasures(data, inferred);

  // No dimension at all — a single summary row, or a row per measure. Each
  // measure becomes a category so the numbers can still be compared.
  if (dimensionColumns.length === 0 && data.rows.length === 1) {
    const row = data.rows[0];
    return {
      categories: measures.map(humanise),
      categoryName: '',
      series: [{ name: 'Value', values: measures.map((m) => toNumber(row[indexOf(m)])) }],
      measureName: '',
      temporal: false,
      rowCount: 1,
      categoryCount: measures.length,
      truncated: false,
      omitted,
    };
  }

  const categoryColumn = pickCategory(data, dimensionColumns, hints);
  const categoryIndex = indexOf(categoryColumn);
  const pivotColumn = hints.seriesBy ?? pickPivot(data, dimensionColumns, categoryColumn, measures);

  const temporal = looksTemporal(data.rows.map((row) => row[categoryIndex]));

  // Categories in first-seen order — the query already sorted them, and
  // re-sorting here would silently override a deliberate ORDER BY.
  const categories: string[] = [];
  const categoryPosition = new Map<string, number>();
  for (const row of data.rows) {
    const key = label(row[categoryIndex]);
    if (!categoryPosition.has(key)) {
      categoryPosition.set(key, categories.length);
      categories.push(key);
    }
  }

  const truncated = categories.length > limit;
  const kept = truncated ? categories.slice(0, limit) : categories;

  const series = pivotColumn
    ? pivotSeries(data, kept, categoryPosition, categoryIndex, indexOf(pivotColumn), indexOf(measures[0]))
    : measures.map((measure) => ({
        name: humanise(measure),
        values: valuesFor(data, kept.length, categoryPosition, categoryIndex, indexOf(measure)),
      }));

  return {
    categories: kept,
    categoryName: humanise(categoryColumn),
    series,
    measureName: series.length === 1 || pivotColumn ? humanise(measures[0]) : '',
    temporal,
    rowCount: data.rows.length,
    categoryCount: categories.length,
    truncated,
    omitted,
  };
};

/**
 * Prefer an explicit hint, then a date-like dimension, then the first dimension
 * that actually distinguishes the rows.
 *
 * That last clause matters more than it looks. Chart 40 ("Category
 * Leaderboard") returns `icon_url` first and every value is null, so taking
 * `dimensions[0]` collapsed ten categories into one and drew a one-row
 * leaderboard — a wrong answer that looked like a real one. A column that is
 * blank, or holds a single value for every row, cannot be the thing being
 * compared.
 */
const pickCategory = (data: ChartData, dimensions: string[], hints: FrameHints): string => {
  if (hints.category && data.columns.includes(hints.category)) return hints.category;

  const valuesOf = (column: string) => data.rows.map((row) => row[data.columns.indexOf(column)]);

  const temporal = dimensions.find((c) => looksTemporal(valuesOf(c)));
  if (temporal) return temporal;

  // Count only rows that actually carry a value. `label()` renders null,
  // undefined and '' all as an em dash, so the previous `distinct.delete('')`
  // removed nothing — a column blank on nine rows out of ten still looked
  // "distinguishing", and became the category with one of its two values
  // literally "—". That is the chart 40 failure again, one row further down.
  const distinguishing = dimensions.find((c) => {
    const present = valuesOf(c).filter((v) => v !== null && v !== undefined && v !== '');
    return new Set(present.map(label)).size > 1;
  });

  return distinguishing ?? dimensions[0] ?? data.columns[0];
};

/**
 * A second dimension becomes series only when it is genuinely a small set of
 * things being compared. Pivoting a high-cardinality column would produce a
 * chart with hundreds of series and a legend nobody can read.
 */
const pickPivot = (
  data: ChartData,
  dimensions: string[],
  categoryColumn: string,
  measures: string[],
): string | null => {
  if (measures.length !== 1) return null;

  for (const column of dimensions) {
    if (column === categoryColumn) continue;
    const index = data.columns.indexOf(column);
    const distinct = new Set(data.rows.map((row) => label(row[index])));
    if (distinct.size > 1 && distinct.size <= MAX_PIVOT_CARDINALITY) return column;
  }
  return null;
};

const valuesFor = (
  data: ChartData,
  size: number,
  positions: Map<string, number>,
  categoryIndex: number,
  measureIndex: number,
): Array<number | null> => {
  const values: Array<number | null> = new Array(size).fill(null);
  for (const row of data.rows) {
    const at = positions.get(label(row[categoryIndex]));
    if (at === undefined || at >= size) continue;
    const value = toNumber(row[measureIndex]);
    // Repeated category with no pivot column: sum, which is what a reader
    // expects from "downloads by country" returning one row per country/day.
    values[at] = value === null ? values[at] : (values[at] ?? 0) + value;
  }
  return values;
};

const pivotSeries = (
  data: ChartData,
  categories: string[],
  positions: Map<string, number>,
  categoryIndex: number,
  pivotIndex: number,
  measureIndex: number,
): Series[] => {
  const byName = new Map<string, Array<number | null>>();

  for (const row of data.rows) {
    const at = positions.get(label(row[categoryIndex]));
    if (at === undefined || at >= categories.length) continue;

    const name = label(row[pivotIndex]);
    let values = byName.get(name);
    if (!values) {
      values = new Array(categories.length).fill(null);
      byName.set(name, values);
    }

    const value = toNumber(row[measureIndex]);
    values[at] = value === null ? values[at] : (values[at] ?? 0) + value;
  }

  return [...byName.entries()].map(([name, values]) => ({ name, values }));
};

/**
 * Caps a frame at `max` series, summing the rest into "Other".
 *
 * The palette has eight validated slots and no ninth, so this is how a chart
 * with more series than colours stays honest — the tail is aggregated, never
 * dropped and never given an invented hue.
 */
export const foldToOther = (frame: Frame, max: number): Frame => {
  if (frame.series.length <= max) return frame;

  const total = (s: Series) => s.values.reduce<number>((acc, v) => acc + (v ?? 0), 0);
  const ranked = [...frame.series].sort((a, b) => total(b) - total(a));
  const head = ranked.slice(0, max - 1);
  const tail = ranked.slice(max - 1);

  const other: Series = {
    name: `Other (${tail.length})`,
    values: frame.categories.map((_, i) =>
      tail.reduce<number | null>((acc, s) => {
        const value = s.values[i];
        return value === null ? acc : (acc ?? 0) + value;
      }, null),
    ),
  };

  return { ...frame, series: [...head, other] };
};

/**
 * The one line a chart prints about what it left out.
 *
 * A chart that quietly drops a column or a tail reads as "this is all the
 * data". Saying so costs one line and is the difference between a summary and
 * a misrepresentation.
 */
export const frameNote = (frame: Frame): string | undefined => {
  const parts: string[] = [];

  if (frame.omitted.length) {
    const names = frame.omitted.map(humanise).join(', ');
    parts.push(
      `${names} ${frame.omitted.length === 1 ? 'is' : 'are'} on a different scale and would not be readable on this axis.`,
    );
  }

  if (frame.truncated) {
    parts.push(
      `Showing the first ${frame.categories.length.toLocaleString('en-GB')} of ${frame.categoryCount.toLocaleString('en-GB')}.`,
    );
  }

  if (!parts.length) return undefined;
  return `${parts.join(' ')} The table view has everything.`;
};

/** Total of a series, ignoring gaps. */
export const sum = (values: Array<number | null>): number =>
  values.reduce<number>((acc, v) => acc + (v ?? 0), 0);

/** The frame has nothing worth drawing. */
export const isEmpty = (frame: Frame): boolean =>
  frame.categories.length === 0 ||
  frame.series.length === 0 ||
  frame.series.every((s) => s.values.every((v) => v === null));
